import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCalendaris,
  useHistorialCalendaris,
  useNoms,
  type HistorialDto,
  type TornDto,
} from '@/api/planificacio';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { Avatar } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { csv, dataCurta, descarregaCsv } from './calendari';
import './planificacio.css';

const BADGE: Record<HistorialDto['accio'], string> = {
  CREAR: 'ok',
  MODIFICAR: 'avis',
  ELIMINAR: 'critic',
  ROTACIO: 'preventiu',
  CANVI_APROVAT: 'crisi',
};

export function W17Historial() {
  const { t } = useTranslation('planificacio');
  const f = useFormat();
  const noms = useNoms();
  const { me } = useMe();
  const calendaris = useCalendaris();
  const [calendariId, setCalendariId] = useState<number | ''>('');
  const [equip, setEquip] = useState(false);
  const llista = useMemo(() => calendaris.data ?? [], [calendaris.data]);
  const ids = llista.map((c) => c.id);
  const historials = useHistorialCalendaris(ids);
  const meves = new Set((me?.arees ?? []).map((a) => a.id));

  const valor = (x?: Partial<TornDto> | null) => {
    if (!x) return '—';
    const persones = [noms(x.titularId), x.substitutId != null ? noms(x.substitutId) : '']
      .filter(Boolean)
      .join(' / ');
    const franja = x.inici && x.fi ? `${f.dataHora(x.inici)} – ${f.dataHora(x.fi)}` : '';
    return [persones, franja].filter(Boolean).join(' · ') || '—';
  };

  const entrades = historials
    .flatMap((h) => h.data ?? [])
    .filter((h) => calendariId === '' || h.calendariId === calendariId)
    .filter((h) => {
      if (!equip) return true;
      const c = llista.find((x) => x.id === h.calendariId);
      return c?.areaId != null && meves.has(c.areaId);
    })
    .sort((a, b) => b.at.localeCompare(a.at));

  const nomCal = (id: number) => llista.find((c) => c.id === id)?.nom ?? `#${id}`;
  const tornAfectat = (h: HistorialDto) => {
    const x = h.despres ?? h.abans;
    const franja =
      x?.inici && x.fi
        ? `${dataCurta(x.inici, f.idioma)} — ${dataCurta(x.fi, f.idioma)}`
        : x?.inici
          ? dataCurta(x.inici, f.idioma)
          : h.tornId != null
            ? `#${h.tornId}`
            : '';
    return [nomCal(h.calendariId), franja].filter(Boolean).join(' · ');
  };

  const exporta = () =>
    descarregaCsv('historial-calendaris.csv', [
      csv([
        t('historial.data'),
        t('historial.autor'),
        t('columnes.calendari'),
        t('historial.torn'),
        t('historial.anterior'),
        t('historial.nou'),
        t('historial.motiu'),
      ]),
      ...entrades.map((h) =>
        csv([
          f.dataHora(h.at),
          h.usuariId != null ? noms(h.usuariId) : t('historial.sistema'),
          nomCal(h.calendariId),
          tornAfectat(h),
          valor(h.abans),
          valor(h.despres),
          t(`accioHistorial.${h.accio}`),
        ]),
      ),
    ]);

  const error = calendaris.isError || historials.some((h) => h.isError);

  return (
    <section className="pantalla" data-screen="W-17">
      <Toolbar titol={t('titols.historial')}>
        <div className="segmented" style={{ width: 260 }}>
          <button
            type="button"
            style={{ flex: 1 }}
            aria-pressed={!equip}
            onClick={() => setEquip(false)}
          >
            {t('historial.tots')}
          </button>
          <button
            type="button"
            style={{ flex: 1 }}
            aria-pressed={equip}
            onClick={() => setEquip(true)}
          >
            {t('historial.equip')}
          </button>
        </div>
        <Desplegable<number | ''>
          variant="chip"
          actiu={calendariId !== ''}
          aria-label={t('columnes.calendari')}
          valor={calendariId}
          onCanvia={setCalendariId}
          opcions={[
            { valor: '', etiqueta: t('historial.filtreCalendari') },
            ...llista.map((c) => ({ valor: c.id, etiqueta: c.nom })),
          ]}
        />
        <button type="button" className="btn btn--s" onClick={exporta} disabled={!entrades.length}>
          <Icona nom="doc-export" />
          {t('comu.exporta')}
        </button>
      </Toolbar>
      <div className="content">
        <div className="panel">
          {error ? (
            <ErrorCarrega onReintenta={() => void calendaris.refetch()} />
          ) : entrades.length === 0 ? (
            <div className="empty">
              <Icona nom="clock-arrow" />
              {calendaris.isLoading ? t('comu.carregant') : t('historial.buit')}
            </div>
          ) : (
            <table className="taula">
              <thead>
                <tr>
                  <th>{t('historial.data')}</th>
                  <th>{t('historial.autor')}</th>
                  <th>{t('historial.torn')}</th>
                  <th>{t('historial.anterior')}</th>
                  <th>{t('historial.nou')}</th>
                  <th>{t('historial.motiu')}</th>
                </tr>
              </thead>
              <tbody>
                {entrades.map((h) => (
                  <tr key={`${h.calendariId}-${h.id}`} data-historial={h.id}>
                    <td className="mono t-caption-2 nowrap">{f.dataHora(h.at)}</td>
                    <td>
                      {h.usuariId != null ? (
                        <span className="hstack gap-8">
                          <Avatar nom={noms(h.usuariId)} />
                          {noms(h.usuariId)}
                        </span>
                      ) : (
                        <span className="hstack gap-8 dim">
                          <span className="avatar bg-gray">
                            <Icona nom="gear" />
                          </span>
                          {t('historial.sistema')}
                        </span>
                      )}
                    </td>
                    <td>{tornAfectat(h)}</td>
                    <td className="dim">{valor(h.abans)}</td>
                    <td>{valor(h.despres)}</td>
                    <td>
                      <span className={`badge badge--${BADGE[h.accio]}`}>
                        {t(`accioHistorial.${h.accio}`)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
}
